import { useRef, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import type {
  Chart, ChartData, ChartOptions, Plugin, RadialLinearScale, TooltipModel,
} from 'chart.js';
import { FloatingTooltip } from '@/components/common/FloatingTooltip';
import { FundamentalIcon } from '@/components/common/FundamentalIcon';
import { buildTooltipPoint, type TooltipPoint } from './buildTooltipPoint';
import './RadarChart.css';

/** Distancia en px más allá del borde de la escala donde se dibuja cada icono. */
const ICON_OFFSET = 22;

interface IconPosition {
  index: number;
  x: number;
  y: number;
}

interface RadarChartProps {
  data: ChartData<'radar'>;
  options: ChartOptions<'radar'>;
  /** Icono por eje, mismo orden y largo que `data.labels`. */
  icons: (string | undefined)[];
}

/**
 * Plugin local (no registrado globalmente) que, en cada `afterDraw`, calcula la
 * posición de cada eje con la propia API de Chart.js (`getPointPosition`, la misma que
 * usa internamente para dibujar `pointLabels`) y la reporta a React para superponer los
 * iconos como una capa HTML.
 */
function createIconOverlayPlugin(onPositions: (positions: IconPosition[]) => void): Plugin<'radar'> {
  return {
    id: 'iconOverlay',
    afterDraw(chart: Chart<'radar'>) {
      const scale = chart.scales.r as RadialLinearScale | undefined;
      if (!scale) return;

      const axisCount = chart.data.labels?.length ?? 0;
      const positions: IconPosition[] = [];
      for (let index = 0; index < axisCount; index += 1) {
        const point = scale.getPointPosition(index, scale.drawingArea + ICON_OFFSET);
        positions.push({ index, x: point.x, y: point.y });
      }
      onPositions(positions);
    },
  };
}

/**
 * Envuelve el radar de Chart.js y reemplaza sus etiquetas de texto por los iconos del
 * catálogo, posicionados con la misma geometría que usaría Chart.js para el texto.
 */
export function RadarChart({ data, options, icons }: RadarChartProps) {
  const chartRef = useRef<Chart<'radar'>>(null);
  const [positions, setPositions] = useState<IconPosition[]>([]);
  const [iconPlugin] = useState(() => createIconOverlayPlugin(setPositions));
  const [tooltipPoint, setTooltipPoint] = useState<TooltipPoint | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());

  const legendEntries = data.datasets.map(dataset => ({
    label: dataset.label ?? '',
    color: typeof dataset.borderColor === 'string' ? dataset.borderColor : '#000',
  }));

  // El tooltip nativo se desactiva en `buildRadarOptions` (`tooltip.enabled: false`);
  // aquí se inyecta el callback `external` que Chart.js sigue invocando con el mismo
  // cálculo de posición/contenido, para pintar el overlay HTML propio (ver
  // buildTooltipPoint.ts sobre por qué no se puede dejar el tooltip nativo).
  const chartOptions: ChartOptions<'radar'> = {
    ...options,
    plugins: {
      ...options.plugins,
      tooltip: {
        ...options.plugins?.tooltip,
        external: context => setTooltipPoint(buildTooltipPoint(context.tooltip as TooltipModel<'radar'>)),
      },
    },
  };

  function toggleDataset(index: number) {
    const willHide = !hiddenDatasets.has(index);
    setHiddenDatasets(prev => {
      const next = new Set(prev);
      if (willHide) next.add(index); else next.delete(index);
      return next;
    });
    chartRef.current?.setDatasetVisibility(index, !willHide);
    chartRef.current?.update();
  }

  return (
    <div className="ud-radar-chart-wrap">
      <div className="ud-radar-chart-canvas">
        <Radar ref={chartRef} data={data} options={chartOptions} plugins={[iconPlugin]} />
        {positions.map(({ index, x, y }) => (
          <FloatingTooltip
            key={index}
            tooltipClassName="ud-radar-icon-tooltip"
            content={String(data.labels?.[index] ?? '')}
          >
            {anchorProps => (
              <div
                {...anchorProps}
                className="ud-radar-icon"
                style={{ left: `${x}px`, top: `${y}px` }}
                tabIndex={0}
                aria-label={String(data.labels?.[index] ?? '')}
              >
                <FundamentalIcon icon={icons[index]} imageClassName="ud-radar-icon-img" />
              </div>
            )}
          </FloatingTooltip>
        ))}
        {tooltipPoint && (
          <div
            className="ud-radar-tooltip"
            style={{ left: `${tooltipPoint.x}px`, top: `${tooltipPoint.y}px` }}
          >
            <strong className="ud-radar-tooltip-title">{tooltipPoint.title}</strong>
            {tooltipPoint.lines.map(line => (
              <div key={line.label} className="ud-radar-tooltip-line">
                <span className="ud-radar-tooltip-dot" style={{ backgroundColor: line.color }} />
                {line.label}: {line.value}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="ud-radar-legend">
        {legendEntries.map((entry, index) => {
          const isHidden = hiddenDatasets.has(index);
          return (
            <button
              key={entry.label}
              type="button"
              className={`ud-radar-legend-item${isHidden ? ' ud-radar-legend-item--hidden' : ''}`}
              onClick={() => toggleDataset(index)}
              aria-pressed={!isHidden}
            >
              <span className="ud-radar-legend-dot" style={{ backgroundColor: entry.color }} />
              {entry.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
